import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ChevronLeft, ChevronRight, ArrowRight, Quote } from "lucide-react";
import { publicApi } from "../lib/api";

const GoogleG = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
    <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z" />
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
  </svg>
);

export const ReviewCard = ({ r, className = "" }) => (
  <div className={`h-full bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex flex-col ${className}`}>
    <div className="flex items-center justify-between mb-4">
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={15} className={i <= Math.round(r.rating || 0) ? "fill-orange text-orange" : "fill-slate-200 text-slate-200"} />)}
      </div>
      <GoogleG />
    </div>
    <Quote size={20} className="text-orange/30 mb-2" />
    <p className="text-slate-600 leading-relaxed text-sm flex-1 line-clamp-6">{r.text}</p>
    <div className="mt-5 flex items-center gap-3">
      {r.profile_photo ? (
        <img loading="lazy" decoding="async" src={r.profile_photo} alt="" className="w-9 h-9 rounded-full object-cover" referrerPolicy="no-referrer" />
      ) : (
        <span className="w-9 h-9 rounded-full bg-navy text-white font-bold flex items-center justify-center text-sm">{(r.author || "?").charAt(0)}</span>
      )}
      <div>
        <p className="font-display font-bold text-navy text-sm leading-tight">{r.author || "Google user"}</p>
        {r.relative_time && <p className="text-xs text-slate-400">{r.relative_time}</p>}
      </div>
    </div>
  </div>
);

export default function GoogleReviewsCarousel({ randomize = false }) {
  const [reviews, setReviews] = useState([]);
  const [meta, setMeta] = useState(null);
  const trackRef = useRef(null);
  useEffect(() => {
    publicApi.googleRating().then((d) => {
      let list = d?.reviews || [];
      if (randomize) list = [...list].sort(() => Math.random() - 0.5);
      setReviews(list.slice(0, 4));
      if (d?.rating) setMeta({ rating: d.rating, total: d.total });
    }).catch(() => {});
  }, [randomize]);

  if (reviews.length === 0) return null;

  const scroll = (dir) => {
    const el = trackRef.current;
    if (el) el.scrollBy({ left: dir * (el.clientWidth * 0.8), behavior: "smooth" });
  };

  return (
    <div data-testid="google-reviews-carousel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <p className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500"><GoogleG /> What our clients say on Google</p>
          {meta && (
            <div className="flex items-center gap-2 mt-1.5" data-testid="reviews-aggregate">
              <span className="flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={16} className={i <= Math.round(meta.rating) ? "fill-orange text-orange" : "fill-slate-200 text-slate-200"} />)}</span>
              <span className="text-sm font-bold text-navy">{meta.rating} out of 5</span>
              {meta.total ? <span className="text-sm text-slate-400">· {meta.total} reviews</span> : null}
            </div>
          )}
        </div>
        <div className="hidden md:flex gap-2">
          <button onClick={() => scroll(-1)} aria-label="Previous" className="w-9 h-9 rounded-full border border-slate-200 hover:border-navy hover:text-navy text-slate-400 flex items-center justify-center transition-colors"><ChevronLeft size={18} /></button>
          <button onClick={() => scroll(1)} aria-label="Next" className="w-9 h-9 rounded-full border border-slate-200 hover:border-navy hover:text-navy text-slate-400 flex items-center justify-center transition-colors"><ChevronRight size={18} /></button>
        </div>
      </div>
      <div ref={trackRef} className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1 scrollbar-hide">
        {reviews.map((r, i) => (
          <div key={i} className="snap-start shrink-0 w-[85%] sm:w-[45%] lg:w-[calc(25%-15px)]">
            <ReviewCard r={r} />
          </div>
        ))}
      </div>
      <div className="text-center mt-8">
        <Link to="/reviews" data-testid="read-more-reviews" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-white font-bold px-7 py-3 rounded-full transition-all hover:-translate-y-0.5">
          Read more reviews <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}
